import { difference, memoize, pickBy } from "lodash";
import type Shopify from "shopify-api-node";
import type { DeepReadonly } from "ts-essentials";
import { nameToApiIdentifier } from "../../ApiIdentified";
import type { ModelIndexEntry } from "../../IndexEntry";
import type { WithoutProblems } from "../../getEnvironmentIndex";
import type { DataModel } from "../../database/DataModel";
import { ShopifyDominionId } from "../../database/DominionLiterals";
import { DEPENDENT_DELETE_META_ACTION_IDENTIFIER, UPSERT_META_ACTION_IDENTIFIER } from "../../database/behaviour/ActionLiterals";
import { FieldType } from "../../type-system/FieldType";
import { type DistributiveOmit, type Thunk, assert, unthunk } from "../../utils";
import type { ConnectionModelSpec } from "../utils";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyApp } from "./ShopifyApp";
import { ShopifyAppCredit } from "./ShopifyAppCredit";
import { ShopifyAppInstallation } from "./ShopifyAppInstallation";
import { ShopifyAppPurchaseOneTime } from "./ShopifyAppPurchaseOneTime";
import { ShopifyAppSubscription } from "./ShopifyAppSubscription";
import { ShopifyAppUsageRecord } from "./ShopifyAppUsageRecord";
import { ShopifyArticle } from "./ShopifyArticle";
import { ShopifyAsset } from "./ShopifyAsset";
import { ShopifyBalanceTransaction } from "./ShopifyBalanceTransaction";
import { ShopifyBillingAddress } from "./ShopifyBillingAddress";
import { ShopifyBlog } from "./ShopifyBlog";
import { ShopifyBulkOperation } from "./ShopifyBulkOperation";
import { ShopifyBusinessEntity } from "./ShopifyBusinessEntity";
import { ShopifyCarrierService } from "./ShopifyCarrierService";
import { ShopifyCart } from "./ShopifyCart";
import { ShopifyCartLineItem } from "./ShopifyCartLineItem";
import { ShopifyCatalog } from "./ShopifyCatalog";
import { ShopifyCheckout } from "./ShopifyCheckout";
import { ShopifyCheckoutAppliedGiftCard } from "./ShopifyCheckoutAppliedGiftCard";
import { ShopifyCheckoutLineItem } from "./ShopifyCheckoutLineItem";
import { ShopifyCheckoutShippingRate } from "./ShopifyCheckoutShippingRate";
import { ShopifyCollect } from "./ShopifyCollect";
import { ShopifyCollection } from "./ShopifyCollection";
import { ShopifyComment } from "./ShopifyComment";
import { ShopifyCompany } from "./ShopifyCompany";
import { ShopifyCompanyAddress } from "./ShopifyCompanyAddress";
import { ShopifyCompanyContact } from "./ShopifyCompanyContact";
import { ShopifyCompanyContactRole } from "./ShopifyCompanyContactRole";
import { ShopifyCompanyContactRoleAssignment } from "./ShopifyCompanyContactRoleAssignment";
import { ShopifyCompanyLocation } from "./ShopifyCompanyLocation";
import { ShopifyCompanyLocationCatalog } from "./ShopifyCompanyLocationCatalog";
import { ShopifyCountry } from "./ShopifyCountry";
import { ShopifyCustomer } from "./ShopifyCustomer";
import { ShopifyCustomerAddress } from "./ShopifyCustomerAddress";
import { ShopifyCustomerMergeable } from "./ShopifyCustomerMergeable";
import { ShopifyCustomerPaymentMethod } from "./ShopifyCustomerPaymentMethod";
import { ShopifyDeliveryMethod } from "./ShopifyDeliveryMethod";
import { ShopifyDiscount } from "./ShopifyDiscount";
import { ShopifyDiscountCode } from "./ShopifyDiscountCode";
import { ShopifyDiscountCustomerBuysCollection } from "./ShopifyDiscountCustomerBuysCollection";
import { ShopifyDiscountCustomerBuysProduct } from "./ShopifyDiscountCustomerBuysProduct";
import { ShopifyDiscountCustomerBuysProductVariant } from "./ShopifyDiscountCustomerBuysProductVariant";
import { ShopifyDiscountCustomerGetsCollection } from "./ShopifyDiscountCustomerGetsCollection";
import { ShopifyDiscountCustomerGetsProduct } from "./ShopifyDiscountCustomerGetsProduct";
import { ShopifyDiscountCustomerGetsProductVariant } from "./ShopifyDiscountCustomerGetsProductVariant";
import { ShopifyDiscountRedeemCode } from "./ShopifyDiscountRedeemCode";
import { ShopifyDispute } from "./ShopifyDispute";
import { ShopifyDisputeEvidence } from "./ShopifyDisputeEvidence";
import { ShopifyDisputeEvidenceFulfillment } from "./ShopifyDisputeEvidenceFullfilment";
import { ShopifyDisputeFileUpload } from "./ShopifyDisputeFileUpload";
import { ShopifyDomain } from "./ShopifyDomain";
import { ShopifyDraftOrder } from "./ShopifyDraftOrder";
import { ShopifyDraftOrderLineItem } from "./ShopifyDraftOrderLineItem";
import { ShopifyDraftOrderPlatformDiscountAllocation } from "./ShopifyDraftOrderPlatformDIscountAllocation";
import { ShopifyDraftOrderPlatformDiscount } from "./ShopifyDraftOrderPlatformDiscount";
import { ShopifyDuty } from "./ShopifyDuty";
import { ShopifyEvent } from "./ShopifyEvent";
import { ShopifyExchangeLineItem } from "./ShopifyExchangeLineItem";
import { ShopifyFile } from "./ShopifyFile";
import { ShopifyFulfillment } from "./ShopifyFulfillment";
import { ShopifyFulfillmentEvent } from "./ShopifyFulfillmentEvent";
import { ShopifyFulfillmentHold } from "./ShopifyFulfillmentHold";
import { ShopifyFulfillmentLineItem } from "./ShopifyFulfillmentLineItem";
import { ShopifyFulfillmentOrder } from "./ShopifyFulfillmentOrder";
import { ShopifyFulfillmentOrderDestination } from "./ShopifyFulfillmentOrderDestination";
import { ShopifyFulfillmentOrderLineItem } from "./ShopifyFulfillmentOrderLineItem";
import { ShopifyFulfillmentOrderMerchantRequest } from "./ShopifyFulfillmentOrderMerchantRequest";
import { ShopifyFulfillmentService } from "./ShopifyFulfillmentService";
import { ShopifyGdprRequest } from "./ShopifyGdprRequest";
import { ShopifyGiftCard } from "./ShopifyGiftCard";
import { ShopifyInventoryItem } from "./ShopifyInventoryItem";
import { ShopifyInventoryLevel } from "./ShopifyInventoryLevel";
import { ShopifyInventoryQuantity } from "./ShopifyInventoryQuantity";
import { ShopifyLocation } from "./ShopifyLocation";
import { ShopifyMarket } from "./ShopifyMarket";
import { ShopifyMarketCatalog } from "./ShopifyMarketCatalog";
import { ShopifyMarketRegion } from "./ShopifyMarketRegion";
import { ShopifyMarketWebPresence } from "./ShopifyMarketWebPresence";
import { ShopifyOrder } from "./ShopifyOrder";
import { ShopifyOrderAdjustment } from "./ShopifyOrderAdjustment";
import { ShopifyOrderLineItem } from "./ShopifyOrderLineItem";
import { ShopifyOrderRisk } from "./ShopifyOrderRisk";
import { ShopifyOrderTransaction } from "./ShopifyOrderTransaction";
import { ShopifyPage } from "./ShopifyPage";
import { ShopifyPaymentSchedule } from "./ShopifyPaymentSchedule";
import { ShopifyPaymentTerms } from "./ShopifyPaymentTerms";
import { ShopifyPaymentsAccount } from "./ShopifyPaymentsAccount";
import { ShopifyPayout } from "./ShopifyPayout";
import { ShopifyPriceList } from "./ShopifyPriceList";
import { ShopifyPriceListPrice } from "./ShopifyPriceListPrice";
import { ShopifyPriceRule } from "./ShopifyPriceRule";
import { ShopifyProduct } from "./ShopifyProduct";
import { ShopifyProductImage } from "./ShopifyProductImage";
import { ShopifyProductMedia } from "./ShopifyProductMedia";
import { ShopifyProductOption } from "./ShopifyProductOption";
import { ShopifyProductVariant } from "./ShopifyProductVariant";
import { ShopifyProductVariantMedia } from "./ShopifyProductVariantMedia";
import { ShopifyProvince } from "./ShopifyProvince";
import { ShopifyQuantityPriceBreak } from "./ShopifyQuantityPriceBreak";
import { ShopifyRedirect } from "./ShopifyRedirect";
import { ShopifyRefund } from "./ShopifyRefund";
import { ShopifyRefundDuty } from "./ShopifyRefundDuty";
import { ShopifyRefundLineItem } from "./ShopifyRefundLineItem";
import type { ShopifyIdentifyByRemoteKeys } from "./ShopifyRemoteIdFieldKeys";
import { REMOTE_IDENTIFICATION_KEYS_FOR_MODEL } from "./ShopifyRemoteIdFieldKeys";
import { ShopifyReturn } from "./ShopifyReturn";
import {
  checkoutIdentifyByRemoteKeys,
  collectIdentifyByRemoteKeys,
  inventoryQuantityIdentifyByRemoteKeys,
} from "./ShopifyVersionedRemoteIdKeys";
import { ShopifyReturnLineItem } from "./ShopifyReturnLineItem";
import { ShopifyReturnReasonDefinition } from "./ShopifyReturnReasonDefinition";
import { ShopifyReturnShippingFee } from "./ShopifyReturnShippingFee";
import { ShopifyReverseDelivery } from "./ShopifyReverseDelivery";
import { ShopifyReverseDeliveryLineItem } from "./ShopifyReverseDeliveryLineItem";
import { ShopifyReverseFulfillmentOrder } from "./ShopifyReverseFulfillmentOrder";
import { ShopifyReverseFulfillmentOrderDisposition } from "./ShopifyReverseFulfillmentOrderDisposition";
import { ShopifyReverseFulfillmentOrderLineItem } from "./ShopifyReverseFulfillmentOrderLineItem";
import { ShopifyScriptTag } from "./ShopifyScriptTag";
import { ShopifySegment } from "./ShopifySegment";
import { ShopifySellingPlan } from "./ShopifySellingPlan";
import { ShopifySellingPlanGroup } from "./ShopifySellingPlanGroup";
import { ShopifySellingPlanGroupProduct } from "./ShopifySellingPlanGroupProduct";
import { ShopifySellingPlanGroupProductVariant } from "./ShopifySellingPlanGroupProductVariant";
import { ShopifyShippingAddress } from "./ShopifyShippingAddress";
import { ShopifyShippingLine } from "./ShopifyShippingLine";
import { ShopifyShop } from "./ShopifyShop";
import { ShopifyStoreCreditAccount } from "./ShopifyStoreCreditAccount";
import { ShopifySubscriptionBillingAttempt } from "./ShopifySubscriptionBillingAttempt";
import { ShopifySubscriptionContract } from "./ShopifySubscriptionContract";
import { ShopifySubscriptionLine } from "./ShopifySubscriptionLine";
import { ShopifySubscriptionManualDiscount } from "./ShopifySubscriptionManualDiscount";
import { ShopifySync } from "./ShopifySync";
import { ShopifyTenderTransaction } from "./ShopifyTenderTransaction";
import { ShopifyTheme } from "./ShopifyTheme";
import {
  ShopifyAppCreditKey,
  ShopifyAppInstallationKey,
  ShopifyAppKey,
  ShopifyAppPurchaseOneTimeKey,
  ShopifyAppSubscriptionKey,
  ShopifyAppUsageRecordKey,
  ShopifyArticleKey,
  ShopifyAssetKey,
  ShopifyAssetToThemeKey,
  ShopifyBalanceTransactionKey,
  ShopifyBillingAddressKey,
  ShopifyBlogKey,
  ShopifyBulkOperationKey,
  ShopifyBusinessEntityKey,
  ShopifyCarrierServiceKey,
  ShopifyCartKey,
  ShopifyCartLineItemKey,
  ShopifyCartLineItemToCartKey,
  ShopifyCatalogKey,
  ShopifyCheckoutAppliedGiftCardKey,
  ShopifyCheckoutAppliedGiftCardToCheckoutKey,
  ShopifyCheckoutKey,
  ShopifyCheckoutLineItemKey,
  ShopifyCheckoutLineItemToCheckoutKey,
  ShopifyCheckoutShippingRateKey,
  ShopifyCheckoutShippingRateToCheckoutKey,
  ShopifyCheckoutToBillingAddressKey,
  ShopifyCheckoutToShippingAddressKey,
  ShopifyCollectKey,
  ShopifyCollectToCollectionKey,
  ShopifyCollectionKey,
  ShopifyCommentKey,
  ShopifyCompanyAddressKey,
  ShopifyCompanyBillingAddressToCompanyLocationKey,
  ShopifyCompanyContactKey,
  ShopifyCompanyContactRoleAssignmentKey,
  ShopifyCompanyContactRoleAssignmentToCompanyLocationKey,
  ShopifyCompanyContactRoleKey,
  ShopifyCompanyContactRoleToCompanyKey,
  ShopifyCompanyKey,
  ShopifyCompanyLocationCatalogKey,
  ShopifyCompanyLocationCatalogToCompanyLocationKey,
  ShopifyCompanyLocationKey,
  ShopifyCountryKey,
  ShopifyCustomerAddressKey,
  ShopifyCustomerAddressToCustomerKey,
  ShopifyCustomerKey,
  ShopifyCustomerMergeableKey,
  ShopifyCustomerMergeableToCustomerKey,
  ShopifyCustomerPaymentMethodKey,
  ShopifyDeliveryMethodKey,
  ShopifyDeliveryMethodToFulfillmentOrderKey,
  ShopifyDiscountCodeKey,
  ShopifyDiscountCustomerBuysCollectionKey,
  ShopifyDiscountCustomerBuysCollectionToDiscountKey,
  ShopifyDiscountCustomerBuysProductKey,
  ShopifyDiscountCustomerBuysProductToDiscountKey,
  ShopifyDiscountCustomerBuysProductVariantKey,
  ShopifyDiscountCustomerBuysProductVariantToDiscountKey,
  ShopifyDiscountCustomerGetsCollectionKey,
  ShopifyDiscountCustomerGetsCollectionToDiscountKey,
  ShopifyDiscountCustomerGetsProductKey,
  ShopifyDiscountCustomerGetsProductToDiscountKey,
  ShopifyDiscountCustomerGetsProductVariantKey,
  ShopifyDiscountCustomerGetsProductVariantToDiscountKey,
  ShopifyDiscountKey,
  ShopifyDiscountRedeemCodeKey,
  ShopifyDiscountRedeemCodeToDiscountKey,
  ShopifyDisputeEvidenceFulfillmentKey,
  ShopifyDisputeEvidenceFulfillmentToDisputeEvidenceKey,
  ShopifyDisputeEvidenceKey,
  ShopifyDisputeEvidenceToDisputeKey,
  ShopifyDisputeFileUploadKey,
  ShopifyDisputeFileUploadToDisputeEvidenceKey,
  ShopifyDisputeKey,
  ShopifyDomainKey,
  ShopifyDraftOrderKey,
  ShopifyDraftOrderLineItemKey,
  ShopifyDraftOrderLineItemToDraftOrderKey,
  ShopifyDraftOrderPlatformDiscountAllocationKey,
  ShopifyDraftOrderPlatformDiscountKey,
  ShopifyDraftOrderPlatformDiscountToDraftOrderPlatformDiscountAllocationKey,
  ShopifyDraftOrderToDraftOrderPlatformDiscountKey,
  ShopifyDutyKey,
  ShopifyDutyToOrderLineItemKey,
  ShopifyEventKey,
  ShopifyExchangeLineItemKey,
  ShopifyExchangeLineItemToReturnKey,
  ShopifyFileKey,
  ShopifyFulfillmentEventKey,
  ShopifyFulfillmentHoldKey,
  ShopifyFulfillmentHoldToFulfillmentOrderKey,
  ShopifyFulfillmentKey,
  ShopifyFulfillmentLineItemKey,
  ShopifyFulfillmentLineItemToFulfillmentKey,
  ShopifyFulfillmentOrderDestinationKey,
  ShopifyFulfillmentOrderDestinationToFulfillmentOrderKey,
  ShopifyFulfillmentOrderKey,
  ShopifyFulfillmentOrderLineItemKey,
  ShopifyFulfillmentOrderLineItemToFulfillmentOrderKey,
  ShopifyFulfillmentOrderMerchantRequestKey,
  ShopifyFulfillmentOrderMerchantRequestToFulfillmentOrderKey,
  ShopifyFulfillmentServiceKey,
  ShopifyGdprRequestKey,
  ShopifyGiftCardKey,
  ShopifyInventoryItemKey,
  ShopifyInventoryLevelKey,
  ShopifyInventoryQuantityKey,
  ShopifyInventoryQuantityToInventoryLevelKey,
  ShopifyLocationKey,
  ShopifyMarketCatalogKey,
  ShopifyMarketCatalogToMarketKey,
  ShopifyMarketKey,
  ShopifyMarketRegionKey,
  ShopifyMarketRegionToMarketKey,
  ShopifyMarketWebPresenceKey,
  ShopifyMarketWebPresenceToMarketKey,
  ShopifyOrderAdjustmentKey,
  ShopifyOrderAdjustmentToRefundKey,
  ShopifyOrderKey,
  ShopifyOrderLineItemKey,
  ShopifyOrderLineItemToOrderKey,
  ShopifyOrderRiskKey,
  ShopifyOrderTransactionKey,
  ShopifyPageKey,
  ShopifyPaymentScheduleKey,
  ShopifyPaymentScheduleToPaymentTermsKey,
  ShopifyPaymentTermsKey,
  ShopifyPaymentsAccountKey,
  ShopifyPayoutKey,
  ShopifyPriceListKey,
  ShopifyPriceListPriceKey,
  ShopifyPriceRuleKey,
  ShopifyProductImageKey,
  ShopifyProductImageToProductKey,
  ShopifyProductKey,
  ShopifyProductMediaKey,
  ShopifyProductMediaToProductKey,
  ShopifyProductOptionKey,
  ShopifyProductOptionToProductKey,
  ShopifyProductVariantKey,
  ShopifyProductVariantMediaKey,
  ShopifyProductVariantMediaToProductVariantKey,
  ShopifyProductVariantToProductKey,
  ShopifyProvinceKey,
  ShopifyQuantityPriceBreakKey,
  ShopifyRedirectKey,
  ShopifyRefundDutyKey,
  ShopifyRefundDutyToRefundKey,
  ShopifyRefundKey,
  ShopifyRefundLineItemKey,
  ShopifyRefundLineItemToRefundKey,
  ShopifyReturnKey,
  ShopifyReturnLineItemKey,
  ShopifyReturnLineItemToReturnKey,
  ShopifyReturnReasonDefinitionKey,
  ShopifyReturnShippingFeeKey,
  ShopifyReturnShippingFeeToReturnKey,
  ShopifyReverseDeliveryKey,
  ShopifyReverseDeliveryLineItemKey,
  ShopifyReverseDeliveryLineItemToReverseDeliveryKey,
  ShopifyReverseDeliveryToReverseFulfillmentOrderKey,
  ShopifyReverseFulfillmentOrderDispositionKey,
  ShopifyReverseFulfillmentOrderDispositionToReverseFulfillmentOrderLineItemKey,
  ShopifyReverseFulfillmentOrderKey,
  ShopifyReverseFulfillmentOrderLineItemKey,
  ShopifyReverseFulfillmentOrderLineItemToReverseFulfillmentOrderKey,
  ShopifyReverseFulfillmentOrderToReturnKey,
  ShopifyScriptTagKey,
  ShopifySegmentKey,
  ShopifySellingPlanGroupKey,
  ShopifySellingPlanGroupProductKey,
  ShopifySellingPlanGroupProductToSellingPlanGroupKey,
  ShopifySellingPlanGroupProductVariantKey,
  ShopifySellingPlanGroupProductVariantToSellingPlanGroupKey,
  ShopifySellingPlanKey,
  ShopifySellingPlanToSellingPlanGroupKey,
  ShopifyShippingAddressKey,
  ShopifyShippingLineKey,
  ShopifyShippingLineToOrderKey,
  ShopifyShopKey,
  ShopifyStoreCreditAccountKey,
  ShopifyStoreCreditAccountToCustomerKey,
  ShopifyStoreCreditAccountToCustomerOwnerKey,
  ShopifySubscriptionBillingAttemptKey,
  ShopifySubscriptionContractKey,
  ShopifySubscriptionLineKey,
  ShopifySubscriptionLineToSubscriptionContractKey,
  ShopifySubscriptionManualDiscountKey,
  ShopifySubscriptionManualDiscountToSubscriptionContractKey,
  ShopifySyncKey,
  ShopifyTenderTransactionKey,
  ShopifyThemeKey,
  shopifyApiIdentifierFromKey,
} from "./keys";
import type { ShopifyAuthenticatedReadWriteScopes, ShopifyAuthenticatedSimpleScopes } from "./scopes";
import { NO_REQUIRED_SCOPE } from "./scopes";
import type { ShopifyWebhookTopic } from "./webhooks";
import { SHOPIFY_WEBHOOKS } from "./webhooks";

/**
 * Check if a connection model spec is disconnected for a given framework version.
 * Encapsulates the common pattern of checking `spec?.isDisconnected ? unthunk(spec.isDisconnected, frameworkVersionConstraint) : false`
 *
 * @param spec - The connection model spec (or partial spec with isDisconnected) to check
 * @param frameworkVersionConstraint - The framework version constraint to check against
 * @returns true if the model is disconnected, false otherwise
 */
export const isModelSpecDisconnected = (
  spec: { isDisconnected?: Thunk<boolean, [string]> } | null | undefined,
  frameworkVersionConstraint: string
): boolean => {
  return spec?.isDisconnected ? unthunk(spec.isDisconnected, frameworkVersionConstraint) : false;
};

/** String union of all the resources we'll sync that have a `list()` method of the api client */
export type RESTListSyncResource = (
  | "balance"
  | "blog"
  | "carrierService"
  | "collect"
  | "comment"
  | "country"
  | "customCollection"
  | "customer"
  | "dispute"
  | "draftOrder"
  | "event"
  | "fulfillmentService"
  | "location"
  | "order"
  | "page"
  | "payout"
  | "priceRule"
  | "product"
  | "redirect"
  | "scriptTag"
  | "smartCollection"
  | "tenderTransaction"
  | "theme"
) &
  keyof Shopify;

/** String union of all the resources we'll sync, whose `list()` methods take an id param pointing at some parent resource. */
export type RESTSubListSyncResource = (
  | "article"
  | "asset"
  | "discountCode"
  | "fulfillmentOrder"
  | "orderRisk"
  | "transaction"
  | "fulfillmentOrderLineItem"
  | "productImage"
) &
  keyof Shopify;
export type RESTSubListParamSyncResource = "collect" & keyof Shopify;
export type RESTNodeListSyncResource = "applicationCharge" | "applicationCredit" | "recurringApplicationCharge" | "usageCharge";
/** String union of the sources which we'll sync using custom logic for the annoying special cases */
export type RESTCustomSyncResource =
  | "app"
  | "appInstallation"
  | "appUsageRecord"
  | "checkout"
  | "domain"
  | "inventory"
  | "shop"
  | "fulfillmentEvents";
export type GraphQLCustomSyncResource =
  | "appUsageRecord"
  | "domain"
  | "app"
  | "appInstallation"
  | "discountNodes"
  | "fulfillmentService"
  | "businessEntities"
  | "shopifyPaymentsAccount"
  | "quantityPriceBreaks"
  | "inventoryQuantities"
  | "shopQuery"
  | "appUsageRecordsQuery";

export type GraphQLListSyncResource =
  | "sellingPlanGroups"
  | "subscriptionContracts"
  | "files"
  | "markets"
  | "companies"
  | "companyLocations"
  | "giftCards"
  | "products"
  | "product.variants"
  | "draftOrders"
  | "locations"
  | "catalogs"
  | "priceLists"
  | "blogs"
  | "blog.articles"
  | "pages"
  | "urlRedirects"
  | "customers"
  | "orders"
  | "balanceTransactions"
  | "payouts"
  | "order.refunds"
  | "order.fulfillments"
  | "order.returns"
  | "return.reverseFulfillmentOrders"
  | "reverseFulfillmentOrder.reverseDeliveries"
  | "tenderTransactions"
  | "fulfillmentOrders"
  | "inventoryItems"
  | "disputes"
  | "events"
  | "collections"
  | "scriptTags"
  | "carrierServices"
  | "themes"
  | "oneTimePurchases"
  | "allSubscriptions"
  | "credits"
  | "segments"
  | "returnReasonDefinitions";

export type GraphQLSubListSyncResource = (
  | "customer.paymentMethods"
  | "sellingPlanGroup.sellingPlans"
  | "subscriptionContract.billingAttempts"
  | "subscriptionContract.discounts"
  | "subscriptionContract.lines"
  | "market.regions"
  | "company.contacts"
  | "company.contactRoles"
  | "companyLocation.roleAssignments"
  | "product.variants"
  | "product.media"
  | "productVariant.media"
  | "draftOrder.lineItems"
  | "customer.storeCreditAccounts"
  | "order.paymentSchedules"
  | "draftOrder.paymentSchedules"
  | "companyLocation.catalogs"
  | "market.catalogs"
  | "market.webPresences"
  | "priceList.prices"
  | "blog.articles"
  | "article.comments"
  | "customer.addressesV2"
  | "order.lineItems"
  | "order.fulfillments"
  | "order.refunds"
  | "order.returns"
  | "return.exchangeLineItems"
  | "return.reverseFulfillmentOrders"
  | "reverseFulfillmentOrder.lineItems"
  | "reverseFulfillmentOrder.reverseDeliveries"
  | "reverseDelivery.reverseDeliveryLineItems"
  | "return.returnLineItems"
  | "refund.orderAdjustments"
  | "refund.refundLineItems"
  | "refund.transactions"
  | "fulfillment.fulfillmentLineItems"
  | "fulfillment.events"
  | "order.shippingLines"
  | "fulfillmentOrder.merchantRequests"
  | "fulfillmentOrder.lineItems"
  | "inventoryItem.inventoryLevels"
  | "theme.files"
  | "companyLocation.storeCreditAccounts"
) &
  `${string}.${string}`;

export type GraphQLNodeListSyncResource = "appCredit" | "appPurchaseOneTime" | "appSubscription";

export type CustomSubListSyncResource = "collects";

export type RESTSyncResource =
  | RESTListSyncResource
  | RESTSubListSyncResource
  | RESTSubListParamSyncResource
  | RESTCustomSyncResource
  | RESTNodeListSyncResource;

export type GraphQLSyncResource =
  | GraphQLListSyncResource
  | GraphQLSubListSyncResource
  | GraphQLNodeListSyncResource
  | GraphQLCustomSyncResource
  | CustomSubListSyncResource;

/**
 * Sync that we run by listing a paginated root level resource
 * That resource may return nested resources within the response
 */
export type RESTListSyncConfig = DeepReadonly<{
  type: "rest_list";
  syncFunction?: string;
  resource: RESTListSyncResource | RESTListSyncResource[];
}>;

/**
 * Sync that we run by listing a nested resource that has an API that requires a parent resource ID
 * The sub resource is the grain of the list, but we need to do the listing API calls in the context of the parent IDs
 */
export type RESTSubListSyncConfig = DeepReadonly<{
  type: "rest_sublist";
  resource: RESTSubListSyncResource;
  parent: RESTListSyncResource | GraphQLListSyncResource;
}>;

/**
 * Like {@link RESTSubListSyncConfig}, expect the parent is specified by a param.
 */
export type RESTSubListParamSyncConfig = DeepReadonly<{
  type: "rest_sublist_param";
  resource: RESTSubListParamSyncResource;
  parent: RESTListSyncResource;
  paramName: string;
  parentFieldIdentifier: string;
  modelKey: string;
}>;

export type CustomSubListSyncConfig = DeepReadonly<{
  type: "custom_sublist";
  parent: GraphQLListSyncResource;
  resource: CustomSubListSyncResource;
}>;

export type GraphQLListSyncConfig = DeepReadonly<{
  type: "graphql_list";
  resource: GraphQLListSyncResource;
  selection: (
    shopifyApiVersion: ShopifyApiVersion,
    enabledModelKeys: string[],
    requestedScopes: string[],
    opts?: { removeGraphQLSelections?: string[] }
  ) => { selection: string };
  filters: {
    updatedAt?: { sortKey: string };
    createdAt?: { sortKey: string };
  };
  sortableBy?: Record<string, { sortKey: string; reverse: boolean }>;
  additionalSelection?: string;
  additionalFields?: string[];
  extraArgs?: Record<string, unknown>;
}>;

type RestOrGraphQLListSyncResource = RESTListSyncResource | GraphQLListSyncResource;

export type GraphQLSubListSyncConfig = DeepReadonly<{
  type: "graphql_sublist";
  resource: GraphQLSubListSyncResource | GraphQLSubListSyncResource[];
  selection: (
    shopifyApiVersion: ShopifyApiVersion,
    enabledModelKeys: string[],
    requestedScopes: string[],
    opts?: { removeGraphQLSelections?: string[] }
  ) => { selection: string };
  parent: RestOrGraphQLListSyncResource | RestOrGraphQLListSyncResource[];
  extraArgs?: Record<string, unknown>;
  limit?: number;
  additionalSelection?: string;
  additionalFields?: string[];
  skipPagination?: boolean;
}>;

export type GraphQLNodeListSyncConfig = DeepReadonly<{
  type: "graphql_node_list";
  resource: GraphQLNodeListSyncResource;
  selection: (shopifyApiVersion: ShopifyApiVersion, enabledModelKeys: string[], requestedScopes: string[]) => { selection: string };
  additionalSelection?: string;
}>;

export type SerializableNodeListSyncConfig = Omit<GraphQLNodeListSyncConfig, "selection"> & {
  selection: string;
};
export type SerializableListSyncConfig = Omit<GraphQLListSyncConfig, "selection"> & {
  selection: string;
};
export type SerializableSubListSyncConfig = Omit<GraphQLSubListSyncConfig, "selection" | "resource"> & {
  selection: string;
  subConfigs?: SerializableSubListSyncConfig[];
  resource: GraphQLSubListSyncResource;
  limit?: number;
  extraArgs?: Record<string, unknown>;
  skipPagination?: boolean;
  additionalFields?: string[];
};

/**
 * Sync that we run a fully custom code handler for because it is a special snowflake.
 */
export type CustomSyncConfig = DeepReadonly<{
  type: "custom";
  resource: RESTCustomSyncResource | GraphQLCustomSyncResource;
  order?: "first" | "last";
}>;

export type InheritSyncConfig = Readonly<{ type: "inherit" }>;

export type WebhookOnlySyncConfig = Readonly<{ type: "webhook_only" }>;

export type SerializableShopifySyncConfig =
  | Exclude<ShopifySyncConfig, GraphQLListSyncConfig | GraphQLSubListSyncConfig | GraphQLNodeListSyncConfig>
  | SerializableListSyncConfig
  | SerializableSubListSyncConfig
  | SerializableNodeListSyncConfig;

export type ShopifySyncConfig =
  | RESTListSyncConfig
  | RESTSubListSyncConfig
  | RESTSubListParamSyncConfig
  | GraphQLListSyncConfig
  | GraphQLSubListSyncConfig
  | GraphQLNodeListSyncConfig
  | CustomSyncConfig
  | CustomSubListSyncConfig
  | InheritSyncConfig
  | WebhookOnlySyncConfig;

type ApiVersionThunkify<T, K extends keyof T> = DistributiveOmit<T, K> & {
  [P in K]: Thunk<T[P], [apiVersion: ShopifyApiVersion]>;
};

export type ShopifyWebhookConfig = {
  skipRegistration?: boolean;
  additionalScopes?: string[] | undefined;
  /** Field names that must be present on the model for this webhook to be registered */
  requiredFieldNames?: string[];
} & (
  | {
      metaActionIdentifier: typeof UPSERT_META_ACTION_IDENTIFIER | typeof DEPENDENT_DELETE_META_ACTION_IDENTIFIER;
      additionalActionIdentifiers?: string[];
      actionIdentifier?: never;
    }
  | {
      metaActionIdentifier?: never;
      additionalActionIdentifiers?: never;
      actionIdentifier: string;
    }
);

type WebhookManifest = Partial<Record<ShopifyWebhookTopic, ShopifyWebhookConfig>>;

export type ShopifyModelMetadata = DeepReadonly<{
  /** Specification for the Shopify model */
  spec: (options: ShopifyModelMetadataSpecOptions) => ConnectionModelSpec | null;

  /**
   * The parent of the Shopify model (if there are multiple parents, we prefer the one from the same scope)
   * Implies that the parent model must be enabled to enable this model, and that we expect to find this model's data nested under the parent model when accessing the parent.
   **/
  parentKey: string | null;
  /**
   * The field key on the parent model from which this model inherts webhooks.
   * When set, this model doesn't register its own webhooks - it uses its parent's webhooks.
   * Whether the model's data actually comes in webhook payloads is determined by
   * checking if the field on the parent model has `availableInWebhook` set.
   */
  inheritWebhooks?: string;

  /**
   * A list of API scopes that are related to accessing this model.
   * See {@link ShopifyAuthenticatedReadWriteScopes} and {@link ShopifyAuthenticatedSimpleScopes} above
   */
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents
  contributingScopes: ((typeof ShopifyAuthenticatedReadWriteScopes)[number] | (typeof ShopifyAuthenticatedSimpleScopes)[number])[];

  /** If the way you access the data requires all of these extra models to be synced, we enforce these dependencies are also added to your connection */
  requireExtraModelKeys?: string[];

  /** If the way you access the data requires some extra models to be synced depending on the scopes selected, we enforce these dependencies are also added to your connection */
  requireExtraModelKeysForScope?: Record<string, string[]>;

  /** If the way you access the data requires extra scopes for syncing, we enforce these scopes are also added to your connection */
  requireExtraScopes?: string[];

  /**
   * Map of shopify webhook topics to the actions or metaactions that will be triggered when the webhook is received
   * When not specified, but the model inherits webhooks, the webhooks will be inherited from the parent model
   * This map is the source of truth for the virtual webhook triggers that are shown for Shopify model actions
   */
  webhooks: WebhookManifest;

  /**
   * A list of remote keys that identify record if the model doesn't have an id field that can be mapped to a Gadget ID
   * Must be one of the predefined identification key arrays from ShopifyRemoteIdFieldKeys
   */
  identifyByRemoteKeys?: ShopifyIdentifyByRemoteKeys | undefined;

  /**
   * Weather or not to trust the `id` field provided by Shopify as a globally unique identifier for this record
   * Is false for some REALLY annoying models
   * @default true
   */
  trustShopifyProvidedId?: boolean;
}>;

export interface ShopifyModelMetadataSpecOptions {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}

export const webhooksForVersion = memoize((apiVersion: ShopifyApiVersion) =>
  Object.entries(SHOPIFY_WEBHOOKS).flatMap(([introducedInVersion, webhooks]) => (introducedInVersion <= apiVersion ? webhooks : []))
);

export const fieldHasWebhookAvailability = (field: {
  contributorConfiguration?: Record<string, { availableInWebhook?: string[] | Record<string, string[]> } | undefined>;
}): boolean => {
  const webhookTopics = field.contributorConfiguration?.[ShopifyDominionId]?.availableInWebhook;
  return !!webhookTopics && (Array.isArray(webhookTopics) ? webhookTopics.length > 0 : Object.keys(webhookTopics).length > 0);
};

/**
 * Finds the field on a parent model spec that corresponds to the given inheritWebhooks field key.
 * The inheritWebhooks key represents the inverse relationship field key on the child model,
 * so we need to find the matching relationship field on the parent.
 */
export const findInheritWebhooksField = (
  parentSpec: ConnectionModelSpec,
  inheritWebhooksFieldKey: string
): ConnectionModelSpec["fields"][number] | undefined => {
  return parentSpec.fields.find((field) => {
    if (field.fieldType === FieldType.BelongsTo) {
      return field.key === inheritWebhooksFieldKey;
    }

    if (field.fieldType === FieldType.HasMany) {
      return field.configuration?.inverseFieldKey === inheritWebhooksFieldKey;
    }

    if (field.fieldType === FieldType.HasOne) {
      return field.configuration?.inverseFieldKey === inheritWebhooksFieldKey;
    }

    if (field.fieldType === FieldType.HasManyThrough) {
      return field.configuration?.inverseJoinModelFieldKey === inheritWebhooksFieldKey;
    }

    return false;
  });
};

export type IgnoreWebhookAvailability = Thunk<
  boolean,
  [modelKey: string, fieldKey: { key?: string | null; shopifyFieldKey?: string | null; graphQLFieldKey?: string | null }]
>;

/** Recursively walk up parent models until a set of webhooks is found for the model */
export const webhooksForModelKey = (
  modelKey: string,
  apiVersion: ShopifyApiVersion,
  frameworkVersionConstraint: string,
  ignoreAvailability: IgnoreWebhookAvailability = false
): WebhookManifest => {
  const metadata = ShopifyModelMetadata[modelKey];

  if (!metadata) {
    return {};
  }

  const inheritWebhooks = metadata.inheritWebhooks && unthunk(metadata.inheritWebhooks, apiVersion);

  if (!inheritWebhooks) {
    const webhooks: WebhookManifest = {};

    for (const [topic, config] of Object.entries(metadata.webhooks ?? {})) {
      webhooks[topic as ShopifyWebhookTopic] = {
        ...config,
        additionalScopes: unthunk(config.additionalScopes, apiVersion),
      };
    }

    const webhooksIncludedInVersion = webhooksForVersion(apiVersion);

    return pickBy(webhooks ?? [], (_, webhook) => webhooksIncludedInVersion.includes(webhook));
  }

  const parentModelKey = metadata.parentKey;
  const parentSpec = shopifyModelSpecForVersion(parentModelKey, apiVersion, frameworkVersionConstraint);

  if (parentSpec) {
    const relatedModelField = findInheritWebhooksField(parentSpec, inheritWebhooks);

    if (relatedModelField) {
      const availability = fieldHasWebhookAvailability(relatedModelField);
      const keys = {
        key: relatedModelField?.key,
        shopifyFieldKey: relatedModelField?.contributorConfiguration?.[ShopifyDominionId]?.shopifyFieldKey,
        graphQLFieldKey: relatedModelField?.contributorConfiguration?.[ShopifyDominionId]?.graphQLFieldKey,
      };
      const shouldIgnoreAvailability = unthunk(ignoreAvailability, parentModelKey, keys);
      if (availability || shouldIgnoreAvailability) {
        return webhooksForModelKey(parentModelKey, apiVersion, frameworkVersionConstraint, ignoreAvailability);
      }
    }
  }
  return {};
};

export const modelHasRequiredFieldForWebhook = (
  model: DataModel | ModelIndexEntry | WithoutProblems<ModelIndexEntry>,
  config: ShopifyWebhookConfig
): boolean => {
  if (!config.requiredFieldNames) {
    return true;
  }

  const modelFieldApiIdentifiers = new Set(model.fieldsAsArray.map((f) => f.apiIdentifier));
  return config.requiredFieldNames.every((fieldName) => {
    const apiIdentifier = nameToApiIdentifier(fieldName);
    return modelFieldApiIdentifiers.has(apiIdentifier);
  });
};

export const upsertWebhookTopicsForModelKey = memoize(
  (modelKey: string, apiVersion: ShopifyApiVersion, frameworkVersionConstraint: string): ShopifyWebhookTopic[] => {
    const metadata = shopifyModelMetadataForVersion(modelKey, apiVersion, frameworkVersionConstraint);

    if (!metadata?.inheritWebhooks) {
      const webhooks = metadata?.webhooks ?? {};

      return Object.entries(webhooks)
        .filter(([_, config]) => config.metaActionIdentifier === UPSERT_META_ACTION_IDENTIFIER && !config.requiredFieldNames)
        .map(([topic]) => topic as ShopifyWebhookTopic);
    }

    if (metadata.parentKey) {
      return upsertWebhookTopicsForModelKey(metadata.parentKey, apiVersion, frameworkVersionConstraint);
    }

    return [];
  },
  (modelKey, apiVersion, frameworkVersionConstraint) => `${modelKey}--${apiVersion}--${frameworkVersionConstraint}`
);

export const isModelMetadataKey = (key: string) => key in ShopifyModelMetadata;

export const shopifyModelSpecOptions: (
  apiVersion: ShopifyApiVersion,
  frameworkVersionConstraint: string
) => ShopifyModelMetadataSpecOptions = (apiVersion: ShopifyApiVersion, frameworkVersionConstraint: string) => ({
  apiVersion,
  frameworkVersionConstraint,
});

export const shopifyConnectionModelSpecsForVersion = (apiVersion: ShopifyApiVersion, frameworkVersionConstraint: string) => {
  return Object.entries(ShopifyModelMetadata)
    .filter(([modelKey]) => shopifyModelExistsInVersion(modelKey, apiVersion, frameworkVersionConstraint))
    .map(([_, metadata]) =>
      assert(
        metadata.spec(shopifyModelSpecOptions(apiVersion, frameworkVersionConstraint)),
        "Could not generate spec for metadata. Verify shopifyModelExistsInVersion check is correct."
      )
    );
};

export const shopifyModelSpecForVersion = memoize(
  (modelKey: string, apiVersion: ShopifyApiVersion, frameworkVersionConstraint: string) => {
    return ShopifyModelMetadata[modelKey]?.spec(shopifyModelSpecOptions(apiVersion, frameworkVersionConstraint));
  },
  (modelKey, apiVersion, frameworkVersionConstraint) => `${modelKey}--${apiVersion}--${frameworkVersionConstraint}`
);

export const shopifyModelExistsInVersion = memoize(
  (
    modelKey: string,
    apiVersion: ShopifyApiVersion,
    frameworkVersionConstraint: string,
    opts: { withDisconnectedModels?: boolean } = { withDisconnectedModels: true }
  ) => {
    const spec = shopifyModelSpecForVersion(modelKey, apiVersion, frameworkVersionConstraint);

    if (!opts.withDisconnectedModels) {
      return !!spec && !isModelSpecDisconnected(spec, frameworkVersionConstraint);
    }
    return !!spec;
  },
  (modelKey, apiVersion, frameworkVersionConstraint, opts) =>
    `${modelKey}--${apiVersion}--${frameworkVersionConstraint}--${opts?.withDisconnectedModels}`
);

export const getDisconnectedShopifyModelsForApiVersion = (apiVersion: ShopifyApiVersion, frameworkVersionConstraint: string): string[] => {
  const disconnectedModels: string[] = [];

  for (const [key, _] of eachShopifyModelMetadataForVersionWithModelKey(apiVersion, frameworkVersionConstraint)) {
    const spec = shopifyModelSpecForVersion(key, apiVersion, frameworkVersionConstraint);
    if (isModelSpecDisconnected(spec, frameworkVersionConstraint)) {
      disconnectedModels.push(key);
    }
  }

  return disconnectedModels;
};

/**
 * Get the Shopify metadata for a given model key and optional api version.
 */
export const shopifyModelMetadataForVersion = memoize(
  (
    modelKey: string,
    apiVersion: ShopifyApiVersion,
    frameworkVersionConstraint: string,
    opts: { withDisconnectedModels?: boolean } = {
      withDisconnectedModels: true,
    }
  ): ShopifyModelMetadata | null => {
    if (shopifyModelExistsInVersion(modelKey, apiVersion, frameworkVersionConstraint, opts)) {
      const metadata = ShopifyModelMetadata[modelKey];

      const webhooks = webhooksForModelKey(modelKey, apiVersion, frameworkVersionConstraint);
      const inheritWebhooks = metadata.inheritWebhooks && unthunk(metadata.inheritWebhooks, apiVersion);

      return {
        ...metadata,
        inheritWebhooks,
        identifyByRemoteKeys:
          ShopifyModelMetadata[modelKey].identifyByRemoteKeys && unthunk(ShopifyModelMetadata[modelKey].identifyByRemoteKeys, apiVersion),
        webhooks,
      };
    }

    return null;
  },
  (modelKey, apiVersion, frameworkVersionConstraint, opts) =>
    `${modelKey}--${apiVersion}--${frameworkVersionConstraint}--${opts?.withDisconnectedModels}`
);

/**
 * Get the key of the root model that webhooks are inherited from.
 * Walks up the parent chain following the `inheritWebhooks` field until
 * a model that owns its own webhooks is found.
 */
export const getRootWebhookModelKey = (modelKey: string): string => {
  if (modelKey in ShopifyModelMetadata) {
    const metadata = ShopifyModelMetadata[modelKey];
    if (metadata.inheritWebhooks && metadata.parentKey) {
      return getRootWebhookModelKey(metadata.parentKey);
    }
  }

  return modelKey;
};

export function* eachShopifyModelMetadataForVersion(
  apiVersion: ShopifyApiVersion,
  frameworkVersionConstraint: string,
  opts: { withDisconnectedModels?: boolean } = { withDisconnectedModels: true }
) {
  for (const key of Object.keys(ShopifyModelMetadata)) {
    const meta = shopifyModelMetadataForVersion(key, apiVersion, frameworkVersionConstraint, opts);
    if (meta) {
      yield meta;
    }
  }
}

export const allShopifyModelApiIdentifiersForVersion = memoize(
  (
    apiVersion: ShopifyApiVersion,
    frameworkVersionConstraint: string,
    opts: { withDisconnectedModels?: boolean } = { withDisconnectedModels: true }
  ) => {
    return Array.from(eachShopifyModelMetadataForVersionWithModelKey(apiVersion, frameworkVersionConstraint, opts)).map(([key]) =>
      shopifyApiIdentifierFromKey(key)
    );
  },
  (apiVersion, frameworkVersionConstraint, opts) => `${apiVersion}--${frameworkVersionConstraint}--${opts?.withDisconnectedModels}`
);

export function* eachShopifyModelMetadataForVersionWithModelKey(
  apiVersion: ShopifyApiVersion,
  frameworkVersionConstraint: string,
  opts: { withDisconnectedModels?: boolean } = { withDisconnectedModels: true }
) {
  for (const key of Object.keys(ShopifyModelMetadata)) {
    const meta = shopifyModelMetadataForVersion(key, apiVersion, frameworkVersionConstraint, opts);
    if (meta) {
      yield [key, meta] as const;
    }
  }
}

export function* eachShopifyModelWebhookTopic() {
  for (const [key, { webhooks }] of Object.entries(ShopifyModelMetadata)) {
    for (const [topic, config] of Object.entries(webhooks)) {
      yield [key, topic as ShopifyWebhookTopic, config] as const;
    }
  }
}

export const ProtectedAccessModels = [
  ShopifyBalanceTransactionKey,
  ShopifyCartKey,
  ShopifyCheckoutKey,
  ShopifyCommentKey,
  ShopifyCustomerKey,
  ShopifyDraftOrderKey,
  ShopifyEventKey,
  ShopifyFulfillmentOrderKey,
  ShopifyOrderKey,
  ShopifyCompanyKey,
];

type InternalModelMetadata = Omit<
  ApiVersionThunkify<ShopifyModelMetadata, "identifyByRemoteKeys">,
  "webhooks" | "parentKey" | "inheritWebhooks"
> & {
  webhooks: Partial<Record<ShopifyWebhookTopic, ApiVersionThunkify<ShopifyWebhookConfig, "additionalScopes">>>;
} & (
    | {
        parentKey: string;
        inheritWebhooks: Thunk<string, [ShopifyApiVersion]>;
      }
    | {
        parentKey: string | null;
        inheritWebhooks?: undefined;
      }
  );

/** Object containing model meta data for all shopify models. Avoid using this object directly and rely on shopifyModelMetadataForVersion */
const ShopifyModelMetadata: Record<string, InternalModelMetadata> = {
  [ShopifyAppKey]: {
    spec: (options) => ShopifyApp(options),
    parentKey: null,
    contributingScopes: [NO_REQUIRED_SCOPE],
    webhooks: {},
  },
  [ShopifyAppCreditKey]: {
    spec: (options) => ShopifyAppCredit(options),
    parentKey: null,
    contributingScopes: [NO_REQUIRED_SCOPE],
    webhooks: {},
  },
  [ShopifyAppInstallationKey]: {
    spec: (options) => ShopifyAppInstallation(options),
    parentKey: null,
    contributingScopes: [NO_REQUIRED_SCOPE],
    webhooks: {},
  },
  [ShopifyCompanyKey]: {
    spec: (options) => ShopifyCompany(options),
    parentKey: null,
    contributingScopes: ["customers"],
    webhooks: {
      "companies/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "companies/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "companies/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyCompanyAddressKey]: {
    spec: (options) => ShopifyCompanyAddress(options),
    parentKey: ShopifyCompanyLocationKey,
    contributingScopes: ["customers"],
    inheritWebhooks: ShopifyCompanyBillingAddressToCompanyLocationKey,
    webhooks: {},
  },
  [ShopifyCompanyContactKey]: {
    spec: (options) => ShopifyCompanyContact(options),
    parentKey: ShopifyCompanyKey,
    contributingScopes: ["customers"],
    webhooks: {
      "company_contacts/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "company_contacts/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "company_contacts/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyCompanyLocationKey]: {
    spec: (options) => ShopifyCompanyLocation(options),
    parentKey: null,
    contributingScopes: ["customers"],
    webhooks: {
      "company_locations/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "company_locations/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "company_locations/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyCompanyContactRoleKey]: {
    spec: (options) => ShopifyCompanyContactRole(options),
    parentKey: ShopifyCompanyKey,
    contributingScopes: ["customers"],
    inheritWebhooks: ShopifyCompanyContactRoleToCompanyKey,
    webhooks: {},
  },
  [ShopifyCompanyContactRoleAssignmentKey]: {
    spec: (options) => ShopifyCompanyContactRoleAssignment(options),
    parentKey: ShopifyCompanyLocationKey,
    contributingScopes: ["customers"],
    inheritWebhooks: ShopifyCompanyContactRoleAssignmentToCompanyLocationKey,
    webhooks: {},
  },
  [ShopifyAppPurchaseOneTimeKey]: {
    spec: (options) => ShopifyAppPurchaseOneTime(options),
    parentKey: null,
    contributingScopes: [NO_REQUIRED_SCOPE],
    webhooks: {
      "app_purchases_one_time/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyAppSubscriptionKey]: {
    spec: (options) => ShopifyAppSubscription(options),
    parentKey: null,
    contributingScopes: [NO_REQUIRED_SCOPE],
    webhooks: {
      "app_subscriptions/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyAppUsageRecordKey]: {
    spec: (options) => ShopifyAppUsageRecord(options),
    parentKey: ShopifyAppSubscriptionKey,
    contributingScopes: [NO_REQUIRED_SCOPE],
    webhooks: {},
  },
  [ShopifyArticleKey]: {
    spec: (options) => ShopifyArticle(options),
    parentKey: ShopifyBlogKey,
    contributingScopes: ["content"],
    webhooks: {},
  },
  [ShopifyAssetKey]: {
    spec: (options) => ShopifyAsset(options),
    parentKey: ShopifyThemeKey,
    contributingScopes: ["themes"],
    webhooks: {},
    inheritWebhooks: ShopifyAssetToThemeKey,
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyAssetKey],
  },
  [ShopifyBalanceTransactionKey]: {
    spec: (options) => ShopifyBalanceTransaction(options),
    parentKey: null,
    contributingScopes: ["read_shopify_payments_payouts"],
    requireExtraScopes: ["read_shopify_payments_accounts"],
    webhooks: {},
  },
  [ShopifyBillingAddressKey]: {
    spec: (options) => ShopifyBillingAddress(options),
    parentKey: ShopifyCheckoutKey,
    contributingScopes: ["checkouts", "orders"],
    inheritWebhooks: ShopifyCheckoutToBillingAddressKey,
    webhooks: {},
  },
  [ShopifyBlogKey]: {
    spec: (options) => ShopifyBlog(options),
    parentKey: null,
    contributingScopes: ["content"],
    webhooks: {},
  },
  [ShopifyBulkOperationKey]: {
    spec: (options) => ShopifyBulkOperation(options),
    parentKey: null,
    contributingScopes: [NO_REQUIRED_SCOPE],
    webhooks: {
      "bulk_operations/finish": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalActionIdentifiers: ["complete", "cancel", "fail", "expire"],
      },
    },
  },
  [ShopifyCarrierServiceKey]: {
    spec: (options) => ShopifyCarrierService(options),
    parentKey: null,
    contributingScopes: ["shipping"],
    webhooks: {},
  },
  [ShopifyCartKey]: {
    spec: (options) => ShopifyCart(options),
    parentKey: null,
    contributingScopes: ["orders"],
    webhooks: {
      "carts/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "carts/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
    },
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyCartKey],
  },
  [ShopifyCartLineItemKey]: {
    spec: (options) => ShopifyCartLineItem(options),
    parentKey: ShopifyCartKey,
    contributingScopes: ["orders"],
    inheritWebhooks: ShopifyCartLineItemToCartKey,
    webhooks: {},
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyCartLineItemKey],
  },
  [ShopifyCatalogKey]: {
    spec: (options) => ShopifyCatalog(options),
    parentKey: null,
    contributingScopes: ["products"],
    webhooks: {},
  },
  [ShopifyCompanyLocationCatalogKey]: {
    spec: (options) => ShopifyCompanyLocationCatalog(options),
    requireExtraScopes: ["read_products"],
    parentKey: ShopifyCompanyLocationKey,
    contributingScopes: ["customers"],
    requireExtraModelKeys: [ShopifyCatalogKey],
    inheritWebhooks: ShopifyCompanyLocationCatalogToCompanyLocationKey,
    webhooks: {},
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyCompanyLocationCatalogKey],
  },
  [ShopifyCheckoutAppliedGiftCardKey]: {
    spec: (options) => ShopifyCheckoutAppliedGiftCard(options),
    parentKey: ShopifyCheckoutKey,
    contributingScopes: ["checkouts", "orders"],
    inheritWebhooks: ShopifyCheckoutAppliedGiftCardToCheckoutKey,
    webhooks: {},
  },
  [ShopifyCheckoutKey]: {
    spec: (options) => ShopifyCheckout(options),
    parentKey: null,
    requireExtraScopes: ["read_orders"],
    contributingScopes: ["checkouts", "orders"],
    identifyByRemoteKeys: checkoutIdentifyByRemoteKeys,
    webhooks: {
      "checkouts/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "checkouts/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "checkouts/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyCheckoutLineItemKey]: {
    spec: (options) => ShopifyCheckoutLineItem(options),
    parentKey: ShopifyCheckoutKey,
    contributingScopes: ["checkouts", "orders"],
    inheritWebhooks: ShopifyCheckoutLineItemToCheckoutKey,
    webhooks: {},
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyCheckoutLineItemKey],
  },
  [ShopifyCheckoutShippingRateKey]: {
    spec: (options) => ShopifyCheckoutShippingRate(options),
    parentKey: ShopifyCheckoutKey,
    contributingScopes: ["checkouts", "orders"],
    webhooks: {},
    inheritWebhooks: ShopifyCheckoutShippingRateToCheckoutKey,
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyCheckoutShippingRateKey],
  },
  [ShopifyCollectionKey]: {
    spec: (options) => ShopifyCollection(options),
    parentKey: null,
    contributingScopes: ["products"],
    webhooks: {
      "collections/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "collections/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "collections/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyCollectKey]: {
    spec: (options) => ShopifyCollect(options),
    parentKey: ShopifyCollectionKey, // and ShopifyProductKey
    contributingScopes: ["products"],
    identifyByRemoteKeys: collectIdentifyByRemoteKeys,
    webhooks: {},
    inheritWebhooks: ShopifyCollectToCollectionKey,
  },
  [ShopifyCommentKey]: {
    spec: (options) => ShopifyComment(options),
    parentKey: ShopifyArticleKey,
    contributingScopes: ["content"],
    webhooks: {},
  },
  [ShopifyCountryKey]: {
    spec: (options) => ShopifyCountry(options),
    parentKey: null,
    contributingScopes: ["shipping"],
    webhooks: {},
  },
  [ShopifyCustomerAddressKey]: {
    spec: (options) => ShopifyCustomerAddress(options),
    parentKey: ShopifyCustomerKey,
    contributingScopes: ["customers"],
    inheritWebhooks: ShopifyCustomerAddressToCustomerKey,
    webhooks: {},
  },
  [ShopifyCustomerMergeableKey]: {
    spec: (options) => ShopifyCustomerMergeable(options),
    parentKey: ShopifyCustomerKey,
    contributingScopes: ["customers"],
    requireExtraScopes: ["read_customer_merge"],
    inheritWebhooks: ShopifyCustomerMergeableToCustomerKey,
    webhooks: {},
  },
  [ShopifyCustomerKey]: {
    spec: (options) => ShopifyCustomer(options),
    parentKey: null,
    contributingScopes: ["customers"],
    webhooks: {
      "customers/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "customers/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "customers/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyCustomerPaymentMethodKey]: {
    spec: (options) => ShopifyCustomerPaymentMethod(options),
    parentKey: null,
    contributingScopes: ["customer_payment_methods"],
    requireExtraScopes: ["read_customers"],
    webhooks: {
      "customer_payment_methods/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "customer_payment_methods/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "customer_payment_methods/revoke": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalActionIdentifiers: ["revoke"],
      },
    },
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyCustomerPaymentMethodKey],
  },
  [ShopifyDiscountCodeKey]: {
    spec: (options) => ShopifyDiscountCode(options),
    parentKey: null,
    contributingScopes: ["discounts"],
    requireExtraScopes: ["read_price_rules"],
    requireExtraModelKeys: [ShopifyPriceRuleKey],
    webhooks: {},
  },
  [ShopifyDiscountKey]: {
    spec: (options) => ShopifyDiscount(options),
    parentKey: null,
    contributingScopes: ["discounts"],
    webhooks: {
      "discounts/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "discounts/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "discounts/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyDiscountRedeemCodeKey]: {
    spec: (options) => ShopifyDiscountRedeemCode(options),
    parentKey: ShopifyDiscountKey,
    contributingScopes: ["discounts"],
    inheritWebhooks: ShopifyDiscountRedeemCodeToDiscountKey,
    webhooks: {},
  },
  [ShopifyDiscountCustomerGetsProductKey]: {
    spec: (options) => ShopifyDiscountCustomerGetsProduct(options),
    parentKey: ShopifyDiscountKey,
    contributingScopes: ["discounts"],
    requireExtraScopes: ["read_products"],
    requireExtraModelKeys: [ShopifyProductKey],
    inheritWebhooks: ShopifyDiscountCustomerGetsProductToDiscountKey,
    webhooks: {},
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyDiscountCustomerGetsProductKey],
  },
  [ShopifyDiscountCustomerBuysProductKey]: {
    spec: (options) => ShopifyDiscountCustomerBuysProduct(options),
    parentKey: ShopifyDiscountKey,
    contributingScopes: ["discounts"],
    requireExtraScopes: ["read_products"],
    requireExtraModelKeys: [ShopifyProductKey],
    inheritWebhooks: ShopifyDiscountCustomerBuysProductToDiscountKey,
    webhooks: {},
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyDiscountCustomerBuysProductKey],
  },
  [ShopifyDiscountCustomerGetsProductVariantKey]: {
    spec: (options) => ShopifyDiscountCustomerGetsProductVariant(options),
    parentKey: ShopifyDiscountKey,
    contributingScopes: ["discounts"],
    requireExtraScopes: ["read_products"],
    requireExtraModelKeys: [ShopifyProductKey, ShopifyProductVariantKey],
    inheritWebhooks: ShopifyDiscountCustomerGetsProductVariantToDiscountKey,
    webhooks: {},
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyDiscountCustomerGetsProductVariantKey],
  },
  [ShopifyDiscountCustomerBuysProductVariantKey]: {
    spec: (options) => ShopifyDiscountCustomerBuysProductVariant(options),
    parentKey: ShopifyDiscountKey,
    contributingScopes: ["discounts"],
    requireExtraScopes: ["read_products"],
    requireExtraModelKeys: [ShopifyProductKey, ShopifyProductVariantKey],
    inheritWebhooks: ShopifyDiscountCustomerBuysProductVariantToDiscountKey,
    webhooks: {},
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyDiscountCustomerBuysProductVariantKey],
  },
  [ShopifyDiscountCustomerGetsCollectionKey]: {
    spec: (options) => ShopifyDiscountCustomerGetsCollection(options),
    parentKey: ShopifyDiscountKey,
    contributingScopes: ["discounts"],
    requireExtraScopes: ["read_products"],
    requireExtraModelKeys: [ShopifyCollectionKey],
    inheritWebhooks: ShopifyDiscountCustomerGetsCollectionToDiscountKey,
    webhooks: {},
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyDiscountCustomerGetsCollectionKey],
  },
  [ShopifyDiscountCustomerBuysCollectionKey]: {
    spec: (options) => ShopifyDiscountCustomerBuysCollection(options),
    parentKey: ShopifyDiscountKey,
    contributingScopes: ["discounts"],
    requireExtraScopes: ["read_products"],
    requireExtraModelKeys: [ShopifyCollectionKey],
    inheritWebhooks: ShopifyDiscountCustomerBuysCollectionToDiscountKey,
    webhooks: {},
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyDiscountCustomerBuysCollectionKey],
  },
  [ShopifyDisputeKey]: {
    spec: (options) => ShopifyDispute(options),
    parentKey: null,
    contributingScopes: ["read_shopify_payments_disputes"],
    webhooks: {
      "disputes/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "disputes/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyDisputeEvidenceKey]: {
    spec: (options) => ShopifyDisputeEvidence(options),
    parentKey: ShopifyDisputeKey,
    contributingScopes: ["read_shopify_payments_disputes"],
    requireExtraScopes: ["read_shopify_payments_dispute_evidences"],
    webhooks: {},
    inheritWebhooks: ShopifyDisputeEvidenceToDisputeKey,
  },
  [ShopifyDisputeFileUploadKey]: {
    spec: (options) => ShopifyDisputeFileUpload(options),
    parentKey: ShopifyDisputeEvidenceKey,
    contributingScopes: ["read_shopify_payments_disputes"],
    requireExtraScopes: ["read_shopify_payments_dispute_evidences"],
    webhooks: {},
    inheritWebhooks: ShopifyDisputeFileUploadToDisputeEvidenceKey,
  },
  [ShopifyDisputeEvidenceFulfillmentKey]: {
    spec: (options) => ShopifyDisputeEvidenceFulfillment(options),
    parentKey: ShopifyDisputeEvidenceKey,
    contributingScopes: ["read_shopify_payments_disputes"],
    requireExtraScopes: ["read_shopify_payments_dispute_evidences"],
    webhooks: {},
    inheritWebhooks: ShopifyDisputeEvidenceFulfillmentToDisputeEvidenceKey,
  },
  [ShopifyDomainKey]: {
    spec: (options) => ShopifyDomain(options),
    parentKey: null,
    contributingScopes: [NO_REQUIRED_SCOPE],
    webhooks: {
      "domains/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "domains/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "domains/destroy": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyDraftOrderKey]: {
    spec: (options) => ShopifyDraftOrder(options),
    parentKey: null,
    contributingScopes: ["draft_orders"],
    webhooks: {
      "draft_orders/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "draft_orders/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "draft_orders/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyDraftOrderLineItemKey]: {
    spec: (options) => ShopifyDraftOrderLineItem(options),
    parentKey: ShopifyDraftOrderKey,
    contributingScopes: ["draft_orders"],
    inheritWebhooks: ShopifyDraftOrderLineItemToDraftOrderKey,
    webhooks: {},
  },
  [ShopifyDraftOrderPlatformDiscountKey]: {
    spec: (options) => ShopifyDraftOrderPlatformDiscount(options),
    parentKey: ShopifyDraftOrderKey,
    contributingScopes: ["draft_orders"],
    inheritWebhooks: ShopifyDraftOrderToDraftOrderPlatformDiscountKey,
    webhooks: {},
  },
  [ShopifyDraftOrderPlatformDiscountAllocationKey]: {
    spec: (options) => ShopifyDraftOrderPlatformDiscountAllocation(options),
    parentKey: ShopifyDraftOrderPlatformDiscountKey,
    contributingScopes: ["draft_orders"],
    inheritWebhooks: ShopifyDraftOrderPlatformDiscountToDraftOrderPlatformDiscountAllocationKey,
    webhooks: {},
  },
  [ShopifyDutyKey]: {
    spec: (options) => ShopifyDuty(options),
    parentKey: ShopifyOrderLineItemKey,
    contributingScopes: ["orders"],
    inheritWebhooks: ShopifyDutyToOrderLineItemKey,
    webhooks: {},
  },
  [ShopifyEventKey]: {
    spec: (options) => ShopifyEvent(options),
    parentKey: null,
    contributingScopes: ["content", "products", "price_rules", "orders"],
    webhooks: {},
  },
  [ShopifyFileKey]: {
    spec: (options) => ShopifyFile(options),
    contributingScopes: ["files", "themes", "products"],
    // We want to only sync this model for ALL files(including non product ones) when this scope is explicitly selected
    requireExtraScopes: ["read_files"],
    parentKey: null,
    webhooks: {},
  },
  [ShopifyFulfillmentEventKey]: {
    spec: (options) => ShopifyFulfillmentEvent(options),
    parentKey: ShopifyFulfillmentKey,
    contributingScopes: ["orders"],
    requireExtraScopes: ["read_fulfillments"],
    webhooks: {
      "fulfillment_events/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "fulfillment_events/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyFulfillmentKey]: {
    spec: (options) => ShopifyFulfillment(options),
    parentKey: ShopifyOrderKey,
    contributingScopes: ["orders"],
    requireExtraScopes: ["read_fulfillments"],
    webhooks: {
      "fulfillments/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "fulfillments/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyFulfillmentLineItemKey]: {
    spec: (options) => ShopifyFulfillmentLineItem(options),
    parentKey: ShopifyFulfillmentKey,
    contributingScopes: ["orders"],
    requireExtraScopes: ["read_fulfillments"],
    inheritWebhooks: ShopifyFulfillmentLineItemToFulfillmentKey,
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyFulfillmentLineItemKey],
    trustShopifyProvidedId: false,
    requireExtraModelKeys: [
      // order line item is required because we need the order line item belongs to be present for the remote key identification
      ShopifyOrderLineItemKey,
    ],
    webhooks: {},
  },
  [ShopifyFulfillmentOrderKey]: {
    spec: (options) => ShopifyFulfillmentOrder(options),
    parentKey: null,
    contributingScopes: ["assigned_fulfillment_orders", "merchant_managed_fulfillment_orders", "third_party_fulfillment_orders"],
    webhooks: {
      "fulfillment_orders/order_routing_complete": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_marketplace_fulfillment_orders", "read_buyer_membership_orders"],
      },
      "fulfillment_orders/fulfillment_request_submitted": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: (apiVersion) => [
          "read_marketplace_fulfillment_orders",
          ...(apiVersion >= "2024-01" ? [] : ["read_buyer_membership_orders"]),
        ],
      },
      "fulfillment_orders/fulfillment_request_accepted": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_marketplace_fulfillment_orders"],
      },
      "fulfillment_orders/fulfillment_request_rejected": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_marketplace_fulfillment_orders"],
      },
      "fulfillment_orders/placed_on_hold": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_marketplace_fulfillment_orders"],
      },
      "fulfillment_orders/cancellation_request_submitted": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_marketplace_fulfillment_orders"],
      },
      "fulfillment_orders/cancellation_request_accepted": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_marketplace_fulfillment_orders"],
      },
      "fulfillment_orders/cancellation_request_rejected": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_marketplace_fulfillment_orders"],
      },
      "fulfillment_orders/cancelled": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_marketplace_fulfillment_orders"],
      },
      "fulfillment_orders/fulfillment_service_failed_to_complete": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_marketplace_fulfillment_orders"],
      },
      "fulfillment_orders/moved": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_marketplace_fulfillment_orders"],
      },
      "fulfillment_orders/split": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: (apiVersion) => (apiVersion >= "2024-01" ? [] : ["read_marketplace_fulfillment_orders"]),
      },
      "fulfillment_orders/merged": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: (apiVersion) => (apiVersion >= "2024-01" ? [] : ["read_marketplace_fulfillment_orders"]),
      },
      "fulfillment_orders/hold_released": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_marketplace_fulfillment_orders"],
      },
      "fulfillment_orders/line_items_prepared_for_local_delivery": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_marketplace_fulfillment_orders"],
      },
      "fulfillment_orders/line_items_prepared_for_pickup": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_marketplace_fulfillment_orders"],
      },
      "fulfillment_orders/rescheduled": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_marketplace_fulfillment_orders"],
      },
      "fulfillment_orders/scheduled_fulfillment_order_ready": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_marketplace_fulfillment_orders"],
      },
      "fulfillment_orders/progress_reported": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "fulfillment_orders/manually_reported_progress_stopped": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyFulfillmentOrderLineItemKey]: {
    spec: (options) => ShopifyFulfillmentOrderLineItem(options),
    parentKey: ShopifyFulfillmentOrderKey,
    contributingScopes: ["assigned_fulfillment_orders", "merchant_managed_fulfillment_orders", "third_party_fulfillment_orders"],
    webhooks: {},
    inheritWebhooks: ShopifyFulfillmentOrderLineItemToFulfillmentOrderKey,
  },
  [ShopifyFulfillmentOrderDestinationKey]: {
    spec: (options) => ShopifyFulfillmentOrderDestination(options),
    parentKey: ShopifyFulfillmentOrderKey,
    contributingScopes: ["assigned_fulfillment_orders", "merchant_managed_fulfillment_orders", "third_party_fulfillment_orders"],
    webhooks: {},
    inheritWebhooks: ShopifyFulfillmentOrderDestinationToFulfillmentOrderKey,
  },
  [ShopifyDeliveryMethodKey]: {
    spec: (options) => ShopifyDeliveryMethod(options),
    parentKey: ShopifyFulfillmentOrderKey,
    contributingScopes: ["assigned_fulfillment_orders", "merchant_managed_fulfillment_orders", "third_party_fulfillment_orders"],
    requireExtraScopes: ["read_orders"],
    webhooks: {},
    inheritWebhooks: ShopifyDeliveryMethodToFulfillmentOrderKey,
  },
  [ShopifyFulfillmentOrderMerchantRequestKey]: {
    spec: (options) => ShopifyFulfillmentOrderMerchantRequest(options),
    parentKey: ShopifyFulfillmentOrderKey,
    contributingScopes: ["assigned_fulfillment_orders", "merchant_managed_fulfillment_orders", "third_party_fulfillment_orders"],
    webhooks: {},
    inheritWebhooks: ShopifyFulfillmentOrderMerchantRequestToFulfillmentOrderKey,
  },
  [ShopifyFulfillmentHoldKey]: {
    spec: (options) => ShopifyFulfillmentHold(options),
    parentKey: ShopifyFulfillmentOrderKey,
    contributingScopes: ["assigned_fulfillment_orders", "merchant_managed_fulfillment_orders", "third_party_fulfillment_orders"],
    webhooks: {},
    inheritWebhooks: ShopifyFulfillmentHoldToFulfillmentOrderKey,
  },
  [ShopifyFulfillmentServiceKey]: {
    spec: (options) => ShopifyFulfillmentService(options),
    parentKey: null,
    contributingScopes: ["fulfillments"],
    webhooks: {},
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyFulfillmentServiceKey],
  },
  [ShopifyGdprRequestKey]: {
    spec: (options) => ShopifyGdprRequest(options),
    parentKey: null,
    contributingScopes: [NO_REQUIRED_SCOPE],
    webhooks: {
      "shop/redact": {
        skipRegistration: true,
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "customers/redact": {
        skipRegistration: true,
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "customers/data_request": {
        skipRegistration: true,
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyGiftCardKey]: {
    spec: (options) => ShopifyGiftCard(options),
    parentKey: null,
    contributingScopes: ["gift_cards"],
    webhooks: {},
  },
  [ShopifyInventoryItemKey]: {
    spec: (options) => ShopifyInventoryItem(options),
    parentKey: null,
    contributingScopes: ["inventory"],
    requireExtraModelKeys: [ShopifyLocationKey],
    requireExtraScopes: ["read_locations"],
    webhooks: {
      "inventory_items/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: (apiVersion) => (apiVersion >= "2024-07" ? ["read_products"] : []),
      },
      "inventory_items/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: (apiVersion) => (apiVersion >= "2024-07" ? ["read_products"] : []),
      },
      "inventory_items/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
        additionalScopes: (apiVersion) => (apiVersion >= "2024-07" ? ["read_products"] : []),
      },
    },
  },
  [ShopifyInventoryLevelKey]: {
    spec: (options) => ShopifyInventoryLevel(options),
    parentKey: ShopifyInventoryItemKey,
    requireExtraModelKeys: [ShopifyLocationKey],
    requireExtraScopes: ["read_locations"],
    contributingScopes: ["inventory"],
    webhooks: {
      "inventory_levels/connect": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "inventory_levels/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "inventory_levels/disconnect": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyInventoryLevelKey],
  },
  [ShopifyInventoryQuantityKey]: {
    spec: (options) => ShopifyInventoryQuantity(options),
    parentKey: ShopifyInventoryLevelKey,
    requireExtraModelKeys: [ShopifyLocationKey],
    contributingScopes: ["inventory"],
    webhooks: {},
    inheritWebhooks: ShopifyInventoryQuantityToInventoryLevelKey,
    identifyByRemoteKeys: inventoryQuantityIdentifyByRemoteKeys,
  },
  [ShopifyLocationKey]: {
    spec: (options) => ShopifyLocation(options),
    parentKey: null,
    contributingScopes: ["locations"],
    webhooks: {
      "locations/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "locations/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "locations/activate": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "locations/deactivate": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "locations/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyMarketKey]: {
    spec: (options) => ShopifyMarket(options),
    parentKey: null,
    contributingScopes: ["markets"],
    webhooks: {
      "markets/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "markets/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "markets/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyMarketRegionKey]: {
    spec: (options) => ShopifyMarketRegion(options),
    parentKey: ShopifyMarketKey,
    contributingScopes: ["markets"],
    inheritWebhooks: ShopifyMarketRegionToMarketKey,
    webhooks: {},
  },
  [ShopifyMarketWebPresenceKey]: {
    spec: (options) => ShopifyMarketWebPresence(options),
    parentKey: ShopifyMarketKey,
    contributingScopes: ["markets"],
    inheritWebhooks: ShopifyMarketWebPresenceToMarketKey,
    webhooks: {},
  },
  [ShopifyMarketCatalogKey]: {
    spec: (options) => ShopifyMarketCatalog(options),
    requireExtraScopes: ["read_products"],
    parentKey: ShopifyMarketKey,
    contributingScopes: ["markets"],
    requireExtraModelKeys: [ShopifyCatalogKey],
    webhooks: {},
    inheritWebhooks: ShopifyMarketCatalogToMarketKey,
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyMarketCatalogKey],
  },
  [ShopifyOrderAdjustmentKey]: {
    spec: (options) => ShopifyOrderAdjustment(options),
    parentKey: ShopifyRefundKey,
    contributingScopes: ["orders"],
    inheritWebhooks: ShopifyOrderAdjustmentToRefundKey,
    webhooks: {},
  },
  [ShopifyOrderKey]: {
    spec: (options) => ShopifyOrder(options),
    parentKey: null,
    contributingScopes: ["orders"],
    webhooks: {
      "orders/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_marketplace_orders"],
      },
      "orders/updated": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalScopes: ["read_buyer_membership_orders", "read_marketplace_orders"],
      },
      "orders/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
      "orders/risk_assessment_changed": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        requiredFieldNames: ["Risk"],
      },
    },
  },
  [ShopifyOrderLineItemKey]: {
    spec: (options) => ShopifyOrderLineItem(options),
    parentKey: ShopifyOrderKey,
    contributingScopes: ["orders"],
    inheritWebhooks: ShopifyOrderLineItemToOrderKey,
    webhooks: {},
  },
  [ShopifyOrderRiskKey]: {
    spec: (options) => ShopifyOrderRisk(options),
    parentKey: ShopifyOrderKey,
    contributingScopes: ["orders"],
    webhooks: {},
  },
  [ShopifyOrderTransactionKey]: {
    spec: (options) => ShopifyOrderTransaction(options),
    parentKey: ShopifyOrderKey,
    contributingScopes: ["orders"],
    webhooks: {
      "order_transactions/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyPageKey]: {
    spec: (options) => ShopifyPage(options),
    parentKey: null,
    contributingScopes: ["content"],
    webhooks: {},
  },
  [ShopifyPaymentTermsKey]: {
    spec: (options) => ShopifyPaymentTerms(options),
    parentKey: null,
    contributingScopes: ["orders", "draft_orders"],
    requireExtraScopes: ["read_payment_terms"],
    requireExtraModelKeysForScope: {
      orders: [ShopifyOrderKey],
      draft_orders: [ShopifyDraftOrderKey],
    },
    webhooks: {
      "payment_terms/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "payment_terms/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "payment_terms/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyPaymentScheduleKey]: {
    spec: (options) => ShopifyPaymentSchedule(options),
    parentKey: ShopifyPaymentTermsKey,
    contributingScopes: ["orders", "draft_orders"],
    requireExtraScopes: ["read_payment_terms"],
    inheritWebhooks: ShopifyPaymentScheduleToPaymentTermsKey,
    webhooks: {},
  },
  [ShopifyPayoutKey]: {
    spec: (options) => ShopifyPayout(options),
    parentKey: null,
    contributingScopes: ["read_shopify_payments_payouts"],
    webhooks: {},
  },
  [ShopifyPriceRuleKey]: {
    spec: (options) => ShopifyPriceRule(options),
    parentKey: null,
    contributingScopes: ["price_rules"],
    webhooks: {},
  },
  [ShopifyPriceListKey]: {
    spec: (options) => ShopifyPriceList(options),
    parentKey: null,
    contributingScopes: ["products"],
    webhooks: {},
  },
  [ShopifyPriceListPriceKey]: {
    spec: (options) => ShopifyPriceListPrice(options),
    parentKey: ShopifyPriceListKey,
    contributingScopes: ["products"],
    webhooks: {},
    requireExtraModelKeys: [ShopifyProductVariantKey],
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyPriceListPriceKey],
  },
  [ShopifyQuantityPriceBreakKey]: {
    spec: (options) => ShopifyQuantityPriceBreak(options),
    parentKey: ShopifyPriceListPriceKey,
    contributingScopes: ["products"],
    webhooks: {},
    requireExtraModelKeys: [ShopifyPriceListKey],
  },
  [ShopifyProductImageKey]: {
    spec: (options) => ShopifyProductImage(options),
    parentKey: ShopifyProductKey,
    contributingScopes: ["products"],
    inheritWebhooks: ShopifyProductImageToProductKey,
    webhooks: {},
  },
  [ShopifyProductKey]: {
    spec: (options) => ShopifyProduct(options),
    parentKey: null,
    contributingScopes: ["products"],
    webhooks: {
      "products/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "products/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "products/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyProductMediaKey]: {
    spec: (options) => ShopifyProductMedia(options),
    parentKey: ShopifyProductKey,
    contributingScopes: ["products"],
    requireExtraModelKeys: [ShopifyFileKey],
    inheritWebhooks: ShopifyProductMediaToProductKey,
    webhooks: {},
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyProductMediaKey],
  },
  [ShopifyProductOptionKey]: {
    spec: (options) => ShopifyProductOption(options),
    parentKey: ShopifyProductKey,
    contributingScopes: ["products"],
    inheritWebhooks: ShopifyProductOptionToProductKey,
    webhooks: {},
  },
  [ShopifyProductVariantKey]: {
    spec: (options) => ShopifyProductVariant(options),
    parentKey: ShopifyProductKey,
    contributingScopes: ["products"],
    inheritWebhooks: ShopifyProductVariantToProductKey,
    webhooks: {},
  },
  [ShopifyProductVariantMediaKey]: {
    spec: (options) => ShopifyProductVariantMedia(options),
    parentKey: ShopifyProductVariantKey,
    contributingScopes: ["products"],
    requireExtraModelKeys: [ShopifyFileKey],
    inheritWebhooks: ShopifyProductVariantMediaToProductVariantKey,
    webhooks: {},
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyProductVariantMediaKey],
  },
  [ShopifyProvinceKey]: {
    spec: (options) => ShopifyProvince(options),
    parentKey: ShopifyCountryKey,
    contributingScopes: ["shipping"],
    webhooks: {},
  },
  [ShopifyRedirectKey]: {
    spec: (options) => ShopifyRedirect(options),
    parentKey: null,
    contributingScopes: ["content"],
    requireExtraScopes: ["read_online_store_navigation"],
    webhooks: {},
  },
  [ShopifyRefundDutyKey]: {
    spec: (options) => ShopifyRefundDuty(options),
    parentKey: ShopifyRefundKey, // Also ShopifyDutyKey
    contributingScopes: ["orders"],
    inheritWebhooks: ShopifyRefundDutyToRefundKey,
    webhooks: {},
  },
  [ShopifyRefundKey]: {
    spec: (options) => ShopifyRefund(options),
    parentKey: ShopifyOrderKey,
    contributingScopes: ["orders"],
    webhooks: {
      "refunds/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyRefundLineItemKey]: {
    spec: (options) => ShopifyRefundLineItem(options),
    parentKey: ShopifyRefundKey,
    contributingScopes: ["orders"],
    inheritWebhooks: ShopifyRefundLineItemToRefundKey,
    webhooks: {},
  },
  [ShopifyReturnKey]: {
    spec: (options) => ShopifyReturn(options),
    parentKey: ShopifyOrderKey,
    contributingScopes: ["orders"],
    requireExtraScopes: ["read_returns"],
    requireExtraModelKeys: [ShopifyOrderKey],
    webhooks: {
      "returns/approve": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "returns/cancel": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "returns/close": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "returns/decline": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "returns/process": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "returns/reopen": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "returns/request": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "returns/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyReturnShippingFeeKey]: {
    spec: (options) => ShopifyReturnShippingFee(options),
    parentKey: ShopifyReturnKey,
    contributingScopes: ["orders"],
    requireExtraScopes: ["read_returns"],
    inheritWebhooks: ShopifyReturnShippingFeeToReturnKey,
    webhooks: {},
  },
  [ShopifyExchangeLineItemKey]: {
    spec: (options) => ShopifyExchangeLineItem(options),
    parentKey: ShopifyReturnKey,
    contributingScopes: ["orders"],
    requireExtraScopes: ["read_returns"],
    webhooks: {},
    inheritWebhooks: ShopifyExchangeLineItemToReturnKey,
  },
  [ShopifyReverseFulfillmentOrderKey]: {
    spec: (options) => ShopifyReverseFulfillmentOrder(options),
    parentKey: ShopifyReturnKey,
    contributingScopes: ["orders"],
    requireExtraScopes: ["read_returns"],
    webhooks: {},
    inheritWebhooks: ShopifyReverseFulfillmentOrderToReturnKey,
  },
  [ShopifyReverseFulfillmentOrderLineItemKey]: {
    spec: (options) => ShopifyReverseFulfillmentOrderLineItem(options),
    parentKey: ShopifyReverseFulfillmentOrderKey,
    contributingScopes: ["orders"],
    requireExtraScopes: ["read_returns"],
    webhooks: {},
    inheritWebhooks: ShopifyReverseFulfillmentOrderLineItemToReverseFulfillmentOrderKey,
  },
  [ShopifyReverseFulfillmentOrderDispositionKey]: {
    spec: (options) => ShopifyReverseFulfillmentOrderDisposition(options),
    parentKey: ShopifyReverseFulfillmentOrderLineItemKey,
    contributingScopes: ["orders"],
    requireExtraScopes: ["read_returns"],
    webhooks: {},
    inheritWebhooks: ShopifyReverseFulfillmentOrderDispositionToReverseFulfillmentOrderLineItemKey,
  },
  [ShopifyReverseDeliveryKey]: {
    spec: (options) => ShopifyReverseDelivery(options),
    parentKey: ShopifyReverseFulfillmentOrderKey,
    contributingScopes: ["orders"],
    requireExtraScopes: ["read_returns"],
    webhooks: {},
    inheritWebhooks: ShopifyReverseDeliveryToReverseFulfillmentOrderKey,
  },
  [ShopifyReverseDeliveryLineItemKey]: {
    spec: (options) => ShopifyReverseDeliveryLineItem(options),
    parentKey: ShopifyReverseDeliveryKey,
    contributingScopes: ["orders"],
    requireExtraScopes: ["read_returns"],
    webhooks: {},
    inheritWebhooks: ShopifyReverseDeliveryLineItemToReverseDeliveryKey,
  },
  [ShopifyReturnLineItemKey]: {
    spec: (options) => ShopifyReturnLineItem(options),
    parentKey: ShopifyReturnKey,
    contributingScopes: ["orders"],
    requireExtraScopes: ["read_returns"],
    webhooks: {},
    inheritWebhooks: ShopifyReturnLineItemToReturnKey,
  },
  [ShopifyReturnReasonDefinitionKey]: {
    spec: (options) => ShopifyReturnReasonDefinition(options),
    parentKey: null,
    contributingScopes: ["returns"],
    webhooks: {},
  },
  [ShopifyScriptTagKey]: {
    spec: (options) => ShopifyScriptTag(options),
    parentKey: null,
    contributingScopes: ["script_tags"],
    webhooks: {},
  },
  [ShopifyShippingAddressKey]: {
    spec: (options) => ShopifyShippingAddress(options),
    parentKey: ShopifyCheckoutKey,
    contributingScopes: ["checkouts", "orders"],
    inheritWebhooks: ShopifyCheckoutToShippingAddressKey,
    webhooks: {},
  },
  [ShopifyShippingLineKey]: {
    spec: (options) => ShopifyShippingLine(options),
    parentKey: ShopifyOrderKey, // Also ShopifyFulfillmentServiceKey
    contributingScopes: ["orders"],
    inheritWebhooks: ShopifyShippingLineToOrderKey,
    webhooks: {},
  },
  [ShopifyShopKey]: {
    spec: (options) => ShopifyShop(options),
    parentKey: null,
    contributingScopes: [NO_REQUIRED_SCOPE], // Special case, shop model is added for all scopes
    webhooks: {
      "shop/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "app/uninstalled": {
        actionIdentifier: "uninstall",
      },
    },
  },
  [ShopifySegmentKey]: {
    spec: (options) => ShopifySegment(options),
    parentKey: null,
    contributingScopes: ["customers"],
    webhooks: {
      "segments/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "segments/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "segments/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifySellingPlanGroupKey]: {
    spec: (options) => ShopifySellingPlanGroup(options),
    parentKey: null,
    contributingScopes: ["products"],
    webhooks: {
      "selling_plan_groups/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "selling_plan_groups/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "selling_plan_groups/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifySellingPlanGroupProductKey]: {
    spec: (options) => ShopifySellingPlanGroupProduct(options),
    parentKey: ShopifySellingPlanGroupKey,
    contributingScopes: ["products"],
    inheritWebhooks: ShopifySellingPlanGroupProductToSellingPlanGroupKey,
    webhooks: {},
  },
  [ShopifySellingPlanGroupProductVariantKey]: {
    spec: (options) => ShopifySellingPlanGroupProductVariant(options),
    parentKey: ShopifySellingPlanGroupKey,
    contributingScopes: ["products"],
    inheritWebhooks: ShopifySellingPlanGroupProductVariantToSellingPlanGroupKey,
    webhooks: {},
  },
  [ShopifySellingPlanKey]: {
    spec: (options) => ShopifySellingPlan(options),
    parentKey: ShopifySellingPlanGroupKey,
    contributingScopes: ["products"],
    inheritWebhooks: ShopifySellingPlanToSellingPlanGroupKey,
    webhooks: {},
  },
  [ShopifyStoreCreditAccountKey]: {
    spec: (options) => ShopifyStoreCreditAccount(options),
    parentKey: ShopifyCustomerKey,
    contributingScopes: ["customers"],
    requireExtraScopes: ["read_store_credit_accounts"],
    inheritWebhooks: (apiVersion) =>
      apiVersion >= "2025-10" ? ShopifyStoreCreditAccountToCustomerOwnerKey : ShopifyStoreCreditAccountToCustomerKey,
    webhooks: {},
  },
  [ShopifySubscriptionBillingAttemptKey]: {
    spec: (options) => ShopifySubscriptionBillingAttempt(options),
    parentKey: ShopifySubscriptionContractKey,
    contributingScopes: ["own_subscription_contracts"],
    webhooks: {
      "subscription_billing_attempts/success": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalActionIdentifiers: ["success"],
      },
      "subscription_billing_attempts/failure": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalActionIdentifiers: ["failure"],
      },
      "subscription_billing_attempts/challenged": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
        additionalActionIdentifiers: ["challenged"],
      },
    },
  },
  [ShopifySubscriptionContractKey]: {
    spec: (options) => ShopifySubscriptionContract(options),
    parentKey: null,
    contributingScopes: ["own_subscription_contracts"],
    webhooks: {
      "subscription_contracts/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      // this also covers subscription_contracts/activate, /expire, /pause, /cancel, /fail
      "subscription_contracts/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifySubscriptionLineKey]: {
    spec: (options) => ShopifySubscriptionLine(options),
    parentKey: ShopifySubscriptionContractKey,
    contributingScopes: ["own_subscription_contracts"],
    webhooks: {},
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifySubscriptionLineKey],
    inheritWebhooks: ShopifySubscriptionLineToSubscriptionContractKey,
  },
  [ShopifySubscriptionManualDiscountKey]: {
    spec: (options) => ShopifySubscriptionManualDiscount(options),
    parentKey: ShopifySubscriptionContractKey,
    contributingScopes: ["own_subscription_contracts"],
    webhooks: {},
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifySubscriptionManualDiscountKey],
    inheritWebhooks: ShopifySubscriptionManualDiscountToSubscriptionContractKey,
  },
  [ShopifySyncKey]: {
    spec: (options) => ShopifySync(options),
    parentKey: null,
    contributingScopes: [NO_REQUIRED_SCOPE], // Special case, model is managed by gadget
    webhooks: {},
  },
  [ShopifyTenderTransactionKey]: {
    spec: (options) => ShopifyTenderTransaction(options),
    parentKey: null,
    contributingScopes: ["orders"],
    webhooks: {
      "tender_transactions/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyThemeKey]: {
    spec: (options) => ShopifyTheme(options),
    parentKey: null,
    contributingScopes: ["themes"],
    webhooks: {
      "themes/create": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "themes/update": {
        metaActionIdentifier: UPSERT_META_ACTION_IDENTIFIER,
      },
      "themes/delete": {
        metaActionIdentifier: DEPENDENT_DELETE_META_ACTION_IDENTIFIER,
      },
    },
  },
  [ShopifyBusinessEntityKey]: {
    spec: (options) => ShopifyBusinessEntity(options),
    parentKey: null,
    contributingScopes: [NO_REQUIRED_SCOPE],
    webhooks: {},
    identifyByRemoteKeys: REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyBusinessEntityKey],
  },
  [ShopifyPaymentsAccountKey]: {
    spec: (options) => ShopifyPaymentsAccount(options),
    parentKey: null,
    contributingScopes: ["read_shopify_payments_accounts"],
    webhooks: {},
  },
};

// A map with topics as keys and model keys as values
export const topicModels = Object.fromEntries(
  Object.entries(ShopifyModelMetadata).flatMap(([key, { webhooks }]) => Object.keys(webhooks).map((topic) => [topic, key]))
);

export const ShopifyModelKeys = Object.keys(ShopifyModelMetadata);

export const isShopifyModelKey = memoize((modelKey: string) => {
  return ShopifyModelKeys.includes(modelKey);
});

export const ShopifyScopelessModelKeys = difference(
  Object.entries(ShopifyModelMetadata)
    .filter(
      ([_, config]) =>
        config.parentKey == null && config.contributingScopes.length == 1 && config.contributingScopes[0] == NO_REQUIRED_SCOPE
    )
    .map(([modelKey]) => modelKey),
  [ShopifyAppKey] // exclude any model key that does not BelongTo a Shop
);

export type ShopifyModelGraphQLNodeMetadata = DeepReadonly<{
  graphQLTypename: string;
  modelKey: string;
  nestedMetafieldResources?: ({
    property: string;
  } & ShopifyModelGraphQLNodeMetadata)[];
}>;

export type ShopifyDiscountNodeType = "DiscountCodeNode" | "DiscountAutomaticNode";

export const ResourceGraphQLMetadata: Partial<
  Record<RESTSyncResource | GraphQLSyncResource | ShopifyDiscountNodeType, ShopifyModelGraphQLNodeMetadata>
> = {
  appInstallation: {
    graphQLTypename: "AppInstallation",
    modelKey: ShopifyAppInstallationKey,
  },
  customer: { graphQLTypename: "Customer", modelKey: ShopifyCustomerKey },
  customers: { graphQLTypename: "Customer", modelKey: ShopifyCustomerKey },
  draftOrder: { graphQLTypename: "DraftOrder", modelKey: ShopifyDraftOrderKey },
  draftOrders: {
    graphQLTypename: "DraftOrder",
    modelKey: ShopifyDraftOrderKey,
  },
  shop: { graphQLTypename: "Shop", modelKey: ShopifyShopKey },
  product: {
    graphQLTypename: "Product",
    modelKey: ShopifyProductKey,
    nestedMetafieldResources: [
      {
        property: "variants",
        graphQLTypename: "ProductVariant",
        modelKey: ShopifyProductVariantKey,
      },
      {
        property: "images",
        graphQLTypename: "ProductImage",
        modelKey: ShopifyProductImageKey,
      },
    ],
  },
  products: {
    graphQLTypename: "Product",
    modelKey: ShopifyProductKey,
    nestedMetafieldResources: [
      {
        property: "variants",
        graphQLTypename: "ProductVariant",
        modelKey: ShopifyProductVariantKey,
      },
      {
        property: "images",
        graphQLTypename: "ProductImage",
        modelKey: ShopifyProductImageKey,
      },
    ],
  },
  "product.variants": {
    graphQLTypename: "ProductVariant",
    modelKey: ShopifyProductVariantKey,
  },
  order: { graphQLTypename: "Order", modelKey: ShopifyOrderKey },
  orders: { graphQLTypename: "Order", modelKey: ShopifyOrderKey },
  location: { graphQLTypename: "Location", modelKey: ShopifyLocationKey },
  locations: { graphQLTypename: "Location", modelKey: ShopifyLocationKey },
  companyLocations: {
    graphQLTypename: "CompanyLocation",
    modelKey: ShopifyCompanyLocationKey,
  },
  customCollection: {
    graphQLTypename: "Collection",
    modelKey: ShopifyCollectionKey,
  },
  smartCollection: {
    graphQLTypename: "Collection",
    modelKey: ShopifyCollectionKey,
  },
  collections: {
    graphQLTypename: "Collection",
    modelKey: ShopifyCollectionKey,
  },
  markets: { graphQLTypename: "Market", modelKey: ShopifyMarketKey },
  companies: { graphQLTypename: "Company", modelKey: ShopifyCompanyKey },
  fulfillmentService: {
    graphQLTypename: "FulfillmentService",
    modelKey: ShopifyFulfillmentServiceKey,
  },
  businessEntities: {
    graphQLTypename: "BusinessEntity",
    modelKey: ShopifyBusinessEntityKey,
  },
  shopifyPaymentsAccount: {
    graphQLTypename: "ShopifyPaymentsAccount",
    modelKey: ShopifyPaymentsAccountKey,
  },
  DiscountCodeNode: {
    graphQLTypename: "DiscountCodeNode",
    modelKey: ShopifyDiscountKey,
  },
  DiscountAutomaticNode: {
    graphQLTypename: "DiscountAutomaticNode",
    modelKey: ShopifyDiscountKey,
  },
  "sellingPlanGroup.sellingPlans": {
    graphQLTypename: "SellingPlan",
    modelKey: ShopifySellingPlanKey,
  },
  blogs: { graphQLTypename: "Blog", modelKey: ShopifyBlogKey },
  "blog.articles": { graphQLTypename: "Article", modelKey: ShopifyArticleKey },
};

/**
 * @internal
 * Only use this function in tests to expose internal model metadata object
 */
export const _dangerouslyAccessShopifyModelMetadata = (fn: (metadata: typeof ShopifyModelMetadata) => void) => {
  fn(ShopifyModelMetadata);
};
